import { moment } from '@hotjar/webapp/lib/moment';
import { uniqWith } from 'lodash-es';
import { DATE_RANGE_TYPE, DATE_UNITS } from '@hotjar/global-filters';
import type { GlobalFilter, QueryMapFilter, QueryLeaf, DateRange } from '@hotjar/global-filters';
import type { DashboardAction } from '@hotjar/dashboard-quantz';
import type { Site } from '@hotjar/site';
import { segment } from '@hotjar/tracking';
import type {
  WidgetConfig,
  WithBasicQueryInfo,
  WithRenderInfo,
} from '@hotjar/dashboard-quantz/src/features/dashboard/model';
import { ChartType, WidgetTemplate } from '@hotjar/dashboard-quantz/src/features/dashboard/model';

export function getDateRangeLabel(dateRange?: DateRange): string {
  if (dateRange == null) {
    return 'no date';
  }
  switch (dateRange.type) {
    case DATE_RANGE_TYPE.RELATIVE: {
      switch (dateRange.unit) {
        case DATE_UNITS.DAYS: {
          return `Last ${dateRange.amount} days`;
        }
        case DATE_UNITS.HOURS: {
          return `Last ${dateRange.amount} hours`;
        }
        case DATE_UNITS.MONTHS: {
          return `Last ${dateRange.amount} months`;
        }
        default:
          return `no date`;
      }
    }
    case DATE_RANGE_TYPE.ABSOLUTE:
      return `custom`;
    case DATE_RANGE_TYPE.ALL:
      return `all date`;
    default:
      return `no date`;
  }
}

export function getAxisResolution(dateRange?: DateRange): string {
  if (dateRange == null) {
    return 'no date';
  }

  switch (dateRange.type) {
    case DATE_RANGE_TYPE.RELATIVE: {
      switch (dateRange.unit) {
        case DATE_UNITS.DAYS: {
          return 'daily';
        }
        case DATE_UNITS.HOURS: {
          return 'hourly';
        }
        case DATE_UNITS.MONTHS: {
          return dateRange.amount <= 6 ? 'weekly' : 'monthly';
        }
        default:
          return `no date`;
      }
    }
    case DATE_RANGE_TYPE.ABSOLUTE: {
      const daysBetween = moment(dateRange.to).diff(dateRange.from, 'days');
      if (daysBetween <= 30) {
        return 'daily';
      }
      if (daysBetween <= 210) {
        return 'weekly';
      }
      return 'monthly';
    }
    default:
      return `no date`;
  }
}

// eslint-disable-next-line func-params-args/func-params
export const trackWidgetViewed = (
  context: {
    site: Site | undefined;
  },
  widgetConfig: WidgetConfig,
  sessionFilters: GlobalFilter,
  additionalData?: Record<string, string>,
): void => {
  if (!context.site) {
    // This shouldn't ever happen, but who knows.
    //
    // We have to account for `site` being potentially undefined otherwise
    // the type-checker won't be happy.
    return;
  }

  const eventData: segment.SegmentTrackData = {
    site_id: context.site.id,
    organization_id: context.site.organization.id,
    account_id: context.site.account.id,
    widget_id: widgetConfig.id,
    widget_title: widgetConfig.title,
    date_range: getDateRangeLabel(sessionFilters.dateRange),
    state_event_was_triggered_on: 'dashboard.widget',
  };

  if (widgetConfig.subtitle) {
    eventData.widget_description = widgetConfig.subtitle;
  }

  // Note(rob.lester): Checking for properties using the `in` operator and
  // then casting to the sub-type as not every WidgetConfig sub-type has
  // these traits.
  //
  // The type checker isn't happy with just indexing the property and
  // checking its undefined, so we check if the property exists on the
  // instance which seems to please the type checker.
  //
  // Another way to do this would be a type-guard function which checks
  // conformity to the WithRenderInfo interface.
  if ('renderInfo' in widgetConfig) {
    const { renderInfo } = widgetConfig as WithRenderInfo;

    eventData.widget_chart_type = renderInfo.chartType;
    eventData.widget_chart_calculation = renderInfo.calculationType;

    if (renderInfo.chartType === ChartType.timeline) {
      eventData.widget_x_axis_resolution = getAxisResolution(sessionFilters.dateRange);
    }
  }

  // `widget_data_queries` is a list of the potential properties and values
  // being queried. This will be a combination of the properties in
  // event and filter-by clauses, as well as aggregation columns
  if ('queryInfo' in widgetConfig) {
    const { queryInfo } = widgetConfig as WithBasicQueryInfo;

    const dataQueries = [];

    if (queryInfo[0].groupBy) {
      dataQueries.push(queryInfo[0].groupBy);
    }

    if (queryInfo[0].eventType) {
      dataQueries.push(queryInfo[0].eventType);
    }

    // Extend dataQueries with values from eventFilters
    // Note: We would need to rewrite this logic and make it more flexible
    // to support more complex filters objects
    const eventFiltersContent = (queryInfo[0].eventFilters as QueryMapFilter)?.EQUAL as QueryLeaf;
    if (eventFiltersContent) {
      // We want to extend with both keys and values as they both might be of interest.
      Array.prototype.push.apply(dataQueries, Object.keys(eventFiltersContent));
      // The values of the object are lists themselves, so flatten them.
      Array.prototype.push.apply(dataQueries, Object.values(eventFiltersContent).flat());
    }

    // Note(rob.lester): We don't add the groupByOptions here, because
    // they're used for the selector but not what is actually being
    // queried.

    eventData.widget_data_queries = dataQueries;
  }

  segment.track('Dashboard Widget Viewed', { ...eventData, ...additionalData });
};

export const trackDashboardViewed = (
  context: {
    site: Site | undefined;
  },
  dashboardConfig: WidgetConfig[],
  sessionFilters: GlobalFilter,
): void => {
  if (!context.site) {
    // This shouldn't ever happen, but who knows.
    //
    // We have to account for `site` being potentially undefined otherwise
    // the type-checker won't be happy.
    return;
  }

  segment.track('Dashboard Viewed', {
    site_id: context.site.id,
    organization_id: context.site.organization.id,
    account_id: context.site.account.id,
    number_widgets_shown: dashboardConfig.length,
    date_range: getDateRangeLabel(sessionFilters.dateRange),
  });

  // Track each widget individually based on the config.
  // Initial `feedbackWidget` tracking should happen separately after feedbacks list query
  dashboardConfig.forEach(
    (config) =>
      ![WidgetTemplate.FeedbackWidget, WidgetTemplate.SessionsWithErrorWidget].includes(
        config.template,
      ) && trackWidgetViewed(context, config, sessionFilters),
  );
};

export function computeChangesSaved(appliedActions: DashboardAction[]): string[] {
  // One change looks like: DashboardActionKind-nbOfWidgets

  const uniqueActions = uniqWith(
    appliedActions,
    (a, b) => a.kind === b.kind && a.widgetId === b.widgetId,
  );

  const map = new Map<string, number>();
  uniqueActions.forEach((item) => {
    const currentCount = map.get(item.kind) ?? 0;
    map.set(item.kind, currentCount + 1);
  });
  return [...map.entries()].map(([kind, count]) => `${kind}-${count}`);
}

export const trackDashboardUpdated = (
  context: {
    site: Site | undefined;
  },
  dashboardConfig: WidgetConfig[],
  appliedActions: DashboardAction[],
): void => {
  if (!context.site) {
    // This shouldn't ever happen, but who knows.
    //
    // We have to account for `site` being potentially undefined otherwise
    // the type-checker won't be happy.
    return;
  }

  segment.track('Dashboard Updated', {
    site_id: context.site.id,
    organization_id: context.site.organization.id,
    account_id: context.site.account.id,
    number_widgets: dashboardConfig.length,
    widget_titles: dashboardConfig.map((widget) => widget.title),
    widget_ids: dashboardConfig.map((widget) => widget.id),
    changes_saved: computeChangesSaved(appliedActions),
  });
};
